/**
 * O token da mesa, lido do QR code.
 *
 * Identifica a mesa, juntamente com uma sessão temporária. O servidor exige-o
 * tanto para criar pedidos como para os ler: sem ele, quem soubesse o slug
 * do restaurante lia os pedidos do dia inteiro, com nomes, totais e o
 * registo de quem os tratou.
 */

const STORAGE_KEY = 'mesa_token_bamidi';
const NUMERO_KEY = 'mesa_bamidi';
const SESSION_KEY = 'sessao_mesa_bamidi';
export const MESA_SESSION_CHANGED = 'bamidi:table-session-changed';
export const MESA_SESSION_EXPIRED = 'bamidi:table-session-expired';
export const MESA_IDLE_MS = 30 * 60_000;
interface MesaSession { token: string; tableToken: string; lastActivity: number }
let memorySession: MesaSession | null = null;
let memoryNumber: string | null = null;

const readSession = (): MesaSession | null => {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(SESSION_KEY) ?? 'null');
    if (!raw || typeof raw !== 'object') return null;
    const item = raw as Partial<MesaSession>;
    return typeof item.token === 'string' && typeof item.tableToken === 'string'
      && typeof item.lastActivity === 'number' && Number.isFinite(item.lastActivity)
      ? item as MesaSession : null;
  } catch { return memorySession; }
};

const ler = (chave: string, fallback: string | null = null): string | null => {
  try {
    return localStorage.getItem(chave);
  } catch {
    return fallback;
  }
};

export const getMesaToken = (): string | null => ler(STORAGE_KEY, memorySession?.tableToken ?? null);

export const getMesaNumero = (): string | null => ler(NUMERO_KEY, memoryNumber);

export const getMesaSessionToken = (): string | null => readSession()?.token ?? null;
export const mesaSessionDeadline = (): number => (readSession()?.lastActivity ?? 0) + MESA_IDLE_MS;
export const mesaSessionExpired = (): boolean => !readSession() || Date.now() >= mesaSessionDeadline();

export const registerMesaActivity = (): boolean => {
  if (mesaSessionExpired()) return false;
  const session = { ...readSession()!, lastActivity: Date.now() };
  memorySession = session;
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch { /* In-memory fallback. */ }
  return true;
};

export const setMesa = (token: string, numero: number | string, sessionToken: string): void => {
  memorySession = { tableToken: token, token: sessionToken, lastActivity: Date.now() };
  memoryNumber = String(numero);
  try {
    localStorage.setItem(STORAGE_KEY, token);
    localStorage.setItem(NUMERO_KEY, String(numero));
    localStorage.setItem(SESSION_KEY, JSON.stringify(memorySession));
  } catch {
    // Modo privado: o pedido em curso ainda funciona, o seguinte pedira o QR.
  }
  window.dispatchEvent(new Event(MESA_SESSION_CHANGED));
};

export const clearMesa = (): void => {
  memorySession = null;
  memoryNumber = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(NUMERO_KEY);
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignorado
  }
};

export const expireMesaSession = (expectedSession = getMesaSessionToken()): void => {
  // A response from an older scan must never log out a newly scanned table.
  if (expectedSession !== getMesaSessionToken()) return;
  const tableToken = getMesaToken();
  clearMesa();
  window.dispatchEvent(new CustomEvent(MESA_SESSION_EXPIRED, { detail: tableToken }));
};
